/**
 * Conversions between the gamification admin API's colour format and the
 * CSS/picker colour format used by the shared `ColorPicker` / `ColorField` /
 * `GradientField` components.
 *
 * The API represents colours as `#AARRGGBB` (9 chars, ALPHA FIRST). CSS, and
 * every colour-picking component in this codebase, expects `#RRGGBB` or
 * `#RRGGBBAA` (alpha LAST, and only present at all when not fully opaque).
 * These two formats are NOT interchangeable byte-for-byte: an 8-digit hex
 * string is valid in both, but means something different depending on which
 * side of the boundary you're on. Feeding an API value straight into the
 * picker or into `background: linear-gradient(...)` silently produces the
 * wrong colour (the alpha byte gets read as part of the hue).
 *
 * Convert at the boundary (API <-> form), keep form/UI state entirely in CSS
 * format.
 */

const ARGB_REGEX = /^#([0-9A-Fa-f]{8})$/
const CSS_HEX_REGEX = /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/

/**
 * `#AARRGGBB` (API, alpha first) -> `#RRGGBB` | `#RRGGBBAA` (CSS/picker,
 * alpha last).
 *
 * A fully-opaque alpha (`FF`) is dropped rather than moved to the end, so the
 * result stays on the picker's 6-digit path instead of round-tripping through
 * an always-visible 8-digit value.
 *
 * Returns `undefined` for undefined/empty/malformed input.
 */
export function argbToCssHex(value?: string): string | undefined {
  if (!value) return undefined
  const match = ARGB_REGEX.exec(value.trim())
  if (!match) return undefined
  const [, argb] = match
  const alpha = argb.slice(0, 2).toUpperCase()
  const rgb = argb.slice(2).toUpperCase()
  return alpha === 'FF' ? `#${rgb}` : `#${rgb}${alpha}`
}

/**
 * Best-effort conversion of a stored tag/theme colour to something CSS can
 * render. Tries the canonical `#AARRGGBB` (alpha-first) interpretation first;
 * if that doesn't match, accepts a value that is already a plain CSS hex
 * (`#RRGGBB` / `#RRGGBBAA`) and returns it as-is. This makes the UI tolerant of
 * rows that predate the alpha-first convention (e.g. colours entered as plain
 * `#RRGGBB` during early API testing) instead of dropping them to no colour.
 *
 * Returns `undefined` only when the value is neither format.
 */
export function toCssColor(value?: string): string | undefined {
  const argb = argbToCssHex(value)
  if (argb) return argb
  if (value && CSS_HEX_REGEX.test(value.trim())) return value.trim().toUpperCase()
  return undefined
}

/**
 * `#RRGGBB` | `#RRGGBBAA` (CSS/picker, alpha last) -> `#AARRGGBB` (API, alpha
 * first). A missing alpha defaults to fully opaque (`FF`).
 *
 * Returns `undefined` for undefined/empty/malformed input.
 */
export function cssHexToArgb(value?: string): string | undefined {
  if (!value) return undefined
  const match = CSS_HEX_REGEX.exec(value.trim())
  if (!match) return undefined
  const hex = match[1]
  const rgb = hex.slice(0, 6)
  const alpha = hex.length === 8 ? hex.slice(6, 8) : 'FF'
  return `#${alpha}${rgb}`.toUpperCase()
}
